import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileSwitcher = ({ onSwitch }) => {
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/profiles/list`);
      setProfiles(res.data.profiles);
      setActiveId(res.data.active_profile_id);
    } catch (err) { /* silent */ }
  };

  const handleSwitch = async (profileId) => {
    if (profileId === activeId) { setOpen(false); return; }
    try {
      await axios.post(`${API}/profiles/${profileId}/switch`);
      setActiveId(profileId);
      setOpen(false);
      toast.success('Profile switched');
      if (onSwitch) onSwitch();
    } catch (err) {
      toast.error('Failed to switch profile');
    }
  };

  const active = profiles.find(p => p.id === activeId);
  if (profiles.length <= 1) return null;

  return (
    <div ref={ref} className="relative" data-testid="profile-switcher">
      <button onClick={() => setOpen(!open)} data-testid="profile-switcher-trigger"
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[#1A1A1A] hover:bg-[#222] border border-[#27272A] transition-all">
        {active?.avatar_url ? (
          <img src={resolveMediaUrl(active.avatar_url)}
            alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-violet-400">{(active?.display_name || active?.username || '?')[0].toUpperCase()}</span>
          </div>
        )}
        <div className="flex-1 text-left min-w-0">
          <p className="text-xs font-medium text-white truncate">{active?.display_name || active?.username}</p>
          <p className="text-[10px] text-[#71717A] truncate">@{active?.username}</p>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-[#71717A] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#141414] border border-[#27272A] rounded-xl shadow-xl z-50 overflow-hidden" data-testid="profile-switcher-dropdown">
          <div className="p-1.5 max-h-64 overflow-y-auto">
            {profiles.map(p => (
              <button key={p.id} onClick={() => handleSwitch(p.id)} data-testid={`switch-profile-${p.username}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
                  p.id === activeId ? 'bg-violet-500/10' : 'hover:bg-white/5'
                }`}>
                {p.avatar_url ? (
                  <img src={resolveMediaUrl(p.avatar_url)}
                    alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-[#27272A] flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-[#A1A1AA]">{(p.display_name || p.username || '?')[0].toUpperCase()}</span>
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-white truncate">{p.display_name || p.username}</p>
                  <p className="text-[10px] text-[#52525B] truncate">@{p.username}</p>
                </div>
                {p.id === activeId && <Check className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSwitcher;
